import { canv } from "@/canv/global";
import { isOnlyCommentWidget } from "@/canv/utils";
import type { Group } from "@/group";
import { transformManager } from "./TransformManager";
import type { CatchPointer } from "./types";
import { calcCandidate, cantShiftByLock, drawRoundedRect } from "./utils";

class SelectionManager {
    clearSelected() {
        this.setSelected([]);
    }

    fixSelected(reason: string, fixItemWrappers = false) {
        canv.eventManager.openRecord();

        for (let i = 0; i < canv.canvasController.selected.length; i++) {
            canv.canvasController.selected[i].fixWidgets(reason);

            if (fixItemWrappers) {
                canv.canvasController.selected[i].fixItemWrappers("rotate");
            }
        }
    }

    chooseSelected(calcContainer: Group | null = null) {
        const foundCandidate = calcCandidate(calcContainer);

        if (foundCandidate == null) {
            this.clearSelected();
            return;
        }

        if (canv.keyboard.isShift()) {
            if (!canv.canvasController.isInSelected(foundCandidate)) {
                if (cantShiftByLock(foundCandidate)) return;

                const newSelected: Group[] = [];

                newSelected.splice(0, 0, ...canv.canvasController.selected);
                newSelected.push(foundCandidate);

                this.setSelected(newSelected);
            } else {
                const newSelected: Group[] = [];

                newSelected.splice(0, 0, ...canv.canvasController.selected);

                for (let i = 0; i < newSelected.length; i++) {
                    if (newSelected[i] === foundCandidate) {
                        newSelected.splice(i, 1);
                    }
                }

                this.setSelected(newSelected);
            }
        } else {
            if (
                canv.canvasController.isInSelected(foundCandidate) &&
                canv.canvasController.selected.length === 1
            ) {
                canv.toolBox
                    .TryGetToolByName("cursorTool")
                    .TryInteractionOnly(foundCandidate, "focus");
            } else {
                this.setSelected([foundCandidate]);
            }
        }
    }

    setSelected(entities: Group[], resetMode = true) {
        // resetMode false используется для selecting
        if (canv.toolBox.currentTool !== canv.toolBox.TryGetToolByName("cursorTool")) {
            canv.toolBox.switchToByName("cursorTool");
            // это просто для прозрачности. Если вы делаем setSelected, то переключаемся на cursorTool, но только если он не выбран.
            // Чтобы не делаеть cursurTool unselect внутри cursorTool.
            // Лучше, чтобы это функция вызывалась в clearSelected и в setSelectedAndInteraction, так будет прозрачнее.
        }

        if (resetMode) canv.toolBox.TryGetToolByName("cursorTool").turnMode("");

        if (entities.length === 1 && entities?.[0]?.onlyChild()?.constructorName === "comment") {
            canv.canvasController.setSelectedGroups(entities);
        } else {
            canv.canvasController.setSelectedGroups(
                entities.filter((entity) => entity?.onlyChild()?.constructorName !== "comment"),
            );
        }

        if (resetMode && !isOnlyCommentWidget()) canv.widgetMenu.reopen();
        if (isOnlyCommentWidget()) {
            canv.widgetMenu.hide();
        }

        canv.intercom.selectMessage(canv.canvasController.selected);
        canv.redraw();
    }

    selecting(isLongSelecting = false) {
        const pt = canv.actionManager.getActionPoint();
        const prePt = canv.actionManager.getPreActionPoint();

        this.selectingWidth += pt.x - prePt.x;
        this.selectingHeight += pt.y - prePt.y;

        if (pt.x >= this.selectingStartX && pt.y >= this.selectingStartY) {
            this.simulatedSelectingStartX = this.selectingStartX;
            this.simulatedSelectingStartY = this.selectingStartY;
        } else if (pt.x < this.selectingStartX && pt.y >= this.selectingStartY) {
            this.simulatedSelectingStartX = this.selectingStartX + this.selectingWidth;
            this.simulatedSelectingStartY = this.selectingStartY;
        } else if (pt.x >= this.selectingStartX && pt.y < this.selectingStartY) {
            this.simulatedSelectingStartX = this.selectingStartX;
            this.simulatedSelectingStartY = this.selectingStartY + this.selectingHeight;
        } else {
            this.simulatedSelectingStartX = this.selectingStartX + this.selectingWidth;
            this.simulatedSelectingStartY = this.selectingStartY + this.selectingHeight;
        }

        this.simulatedSelectingWidth = Math.abs(this.selectingWidth);
        this.simulatedSelectingHeight = Math.abs(this.selectingHeight);

        const selectedGroups = new Set<Group>();
        const area = {
            x: this.simulatedSelectingStartX,
            y: this.simulatedSelectingStartY,
            width: this.simulatedSelectingWidth,
            height: this.simulatedSelectingHeight,
        };

        const candidateItems =
            canv.canvasController.hashGrid?.queryArea({
                startX: area.x,
                startY: area.y,
                width: area.width,
                height: area.height,
            }) || [];

        for (let i = 0; i < candidateItems.length; i++) {
            if (canv.canvasController.selected.length === 0) {
                const isInteraction = canv.toolBox
                    .TryGetToolByName("cursorTool")
                    .TryInteractionOnly(candidateItems[i], "selecting");

                if (isInteraction) {
                    // если кто-то согласился на interaction, то мы переключаемся на него
                    canv.redraw();
                    return;
                }
            }

            if (
                candidateItems[i].isAnyItemInArea(area) &&
                (isLongSelecting ? candidateItems[i].isGroupInArea(area, true) : true) &&
                !candidateItems[i].isLocked
            ) {
                const itemOfGroup =
                    candidateItems[i].parent && candidateItems[i]?.parent?.type === "group"
                        ? candidateItems[i].parent //возможно у старых групп где их глубина была не лимитирована будут тут проблемы
                        : candidateItems[i];

                selectedGroups.add(itemOfGroup as Group);
            }
        }

        if (!canv.canvasController.isEqualToSelected(Array.from(selectedGroups))) {
            this.setSelected([...selectedGroups], false);
        }

        canv.redraw();
    }

    startSelecting() {
        const pt = canv.actionManager.getActionPoint();

        this.selectingStartX = pt.x;
        this.selectingStartY = pt.y;

        this.selectingWidth = 0;
        this.selectingHeight = 0;

        this.simulatedSelectingStartX = this.selectingStartX;
        this.simulatedSelectingStartY = this.selectingStartY;
        this.simulatedSelectingWidth = this.selectingWidth;
        this.simulatedSelectingHeight = this.selectingHeight;
    }

    isOverSelected() {
        if (canv.canvasController.selected.length === 0) {
            return false;
        }

        if (
            canv.canvasController.selected.length === 1 &&
            canv.canvasController.selected[0].isItemWrapper()
        ) {
            return canv.canvasController.selected[0].isOverAnyInGroup();
        }

        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        if (
            x >= canv.canvasController.startX &&
            x <= canv.canvasController.startX + canv.canvasController.width &&
            y >= canv.canvasController.startY &&
            y <= canv.canvasController.startY + canv.canvasController.height
        ) {
            return true;
        }

        return false;
    }

    isOverCatchers() {
        if (canv.canvasController.selected.length === 0) {
            return false;
        }
        if (canv.toolBox.TryGetToolByName("cursorTool").isNothingWhenInteraction().status)
            return false;

        if (
            canv.canvasController.selected.length === 1 &&
            canv.canvasController.selected[0].isItemWrapper() &&
            canv.canvasController.selected[0].onlyChild()?.customResize
        ) {
            const result = canv.canvasController.selected[0].onlyChild()?.isCatched();

            if (result) {
                transformManager.setCatchPointer(
                    canv.canvasController.selected[0].onlyChild()
                        ?.catchPointer as unknown as CatchPointer,
                );
                return true;
            }

            return false;
        }

        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        if (
            x > canv.canvasController.startX - this.pointerSize / 2 &&
            x < canv.canvasController.startX + this.pointerSize / 2 &&
            y > canv.canvasController.startY - this.pointerSize / 2 &&
            y < canv.canvasController.startY + this.pointerSize / 2
        ) {
            transformManager.setCatchPointer("upLeft");
            return true;
        }

        if (
            x > canv.canvasController.startX + canv.canvasController.width - this.pointerSize / 2 &&
            x < canv.canvasController.startX + canv.canvasController.width + this.pointerSize / 2 &&
            y > canv.canvasController.startY - this.pointerSize / 2 &&
            y < canv.canvasController.startY + this.pointerSize / 2
        ) {
            transformManager.setCatchPointer("upRight");
            return true;
        }

        if (
            x > canv.canvasController.startX + canv.canvasController.width - this.pointerSize / 2 &&
            x < canv.canvasController.startX + canv.canvasController.width + this.pointerSize / 2 &&
            y >
                canv.canvasController.startY +
                    canv.canvasController.height -
                    this.pointerSize / 2 &&
            y < canv.canvasController.startY + canv.canvasController.height + this.pointerSize / 2
        ) {
            transformManager.setCatchPointer("bottomRight");
            return true;
        }

        if (
            x > canv.canvasController.startX - this.pointerSize / 2 &&
            x < canv.canvasController.startX + this.pointerSize / 2 &&
            y >
                canv.canvasController.startY +
                    canv.canvasController.height -
                    this.pointerSize / 2 &&
            y < canv.canvasController.startY + canv.canvasController.height + this.pointerSize / 2
        ) {
            transformManager.setCatchPointer("bottomLeft");
            return true;
        }

        return false;
    }

    isOverSide() {
        if (canv.canvasController.selected.length === 0) {
            return false;
        }
        if (canv.toolBox.TryGetToolByName("cursorTool").isNothingWhenInteraction().status)
            return false;

        if (
            canv.canvasController.selected.length === 1 &&
            canv.canvasController.selected[0].isItemWrapper() &&
            canv.canvasController.selected[0].onlyChild()?.customResize
        ) {
            if (!canv.canvasController.selected[0].onlyChild()?.allowSideResize) {
                return false;
            }
        }

        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        const allowArea = canv.actionManager.getZoomConstant(2.5) / 2;

        if (
            x > canv.canvasController.startX - allowArea &&
            x < canv.canvasController.startX + allowArea &&
            y > canv.canvasController.startY &&
            y < canv.canvasController.startY + canv.canvasController.height
        ) {
            transformManager.setCatchPointer("sideLeft");
            return true;
        }

        if (
            x > canv.canvasController.startX + canv.canvasController.width - allowArea &&
            x < canv.canvasController.startX + canv.canvasController.width + allowArea &&
            y > canv.canvasController.startY &&
            y < canv.canvasController.startY + canv.canvasController.height
        ) {
            transformManager.setCatchPointer("sideRight");
            return true;
        }

        if (
            x > canv.canvasController.startX &&
            x < canv.canvasController.startX + canv.canvasController.width &&
            y > canv.canvasController.startY - allowArea &&
            y < canv.canvasController.startY + allowArea
        ) {
            transformManager.setCatchPointer("sideTop");
            return true;
        }

        if (
            x > canv.canvasController.startX &&
            x < canv.canvasController.startX + canv.canvasController.width &&
            y > canv.canvasController.startY + canv.canvasController.height - allowArea &&
            y < canv.canvasController.startY + canv.canvasController.height + allowArea
        ) {
            transformManager.setCatchPointer("sideBottom");
            return true;
        }

        return false;
    }

    isOverRotater() {
        if (canv.canvasController.selected.length === 0) {
            return false;
        }
        if (
            canv.canvasController.selected.length === 1 &&
            canv.canvasController.selected[0].isItemWrapper() &&
            !canv.canvasController.selected[0].onlyChild()?.rotatable
        ) {
            return false;
        }

        if (canv.toolBox.TryGetToolByName("cursorTool").isNothingWhenInteraction().status)
            return false;

        const squareSize = canv.actionManager.getZoomConstant(25);

        // если слишком маленький элемент выделен - не показываем курсор
        if (canv.canvasController.height < squareSize && canv.canvasController.width < squareSize) {
            return false;
        }

        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        if (
            x > canv.canvasController.startX - squareSize &&
            x < canv.canvasController.startX &&
            y > canv.canvasController.startY - squareSize &&
            y < canv.canvasController.startY
        ) {
            transformManager.setCatchPointer("upLeft");
            return true;
        }

        if (
            x > canv.canvasController.startX + canv.canvasController.width &&
            x < canv.canvasController.startX + canv.canvasController.width + squareSize &&
            y > canv.canvasController.startY - squareSize &&
            y < canv.canvasController.startY
        ) {
            transformManager.setCatchPointer("upRight");
            return true;
        }

        if (
            x > canv.canvasController.startX - squareSize &&
            x < canv.canvasController.startX &&
            y > canv.canvasController.startY + canv.canvasController.height &&
            y < canv.canvasController.startY + canv.canvasController.height + squareSize
        ) {
            transformManager.setCatchPointer("bottomLeft");
            return true;
        }

        if (
            x > canv.canvasController.startX + canv.canvasController.width &&
            x < canv.canvasController.startX + canv.canvasController.width + squareSize &&
            y > canv.canvasController.startY + canv.canvasController.height &&
            y < canv.canvasController.startY + canv.canvasController.height + squareSize
        ) {
            transformManager.setCatchPointer("bottomRight");
            return true;
        }

        return false;
    }

    drawCatchPointer() {
        if (canv.canvasController.selected.length === 0) return;

        if (
            canv.canvasController.selected.length === 1 &&
            canv.canvasController.selected[0].isItemWrapper() &&
            canv.canvasController.selected[0].onlyChild()?.customResize
        ) {
            canv.canvasController.selected[0].onlyChild()?.showCatchPointer();
            return;
        }

        this.pointerSize = (12.5 / canv.m[0]) * canv.dpr;

        if (this.pointerSize < 4) {
            this.pointerSize = 4;
        }

        if (
            this.pointerSize >= canv.canvasController.width ||
            this.pointerSize >= canv.canvasController.height
        ) {
            this.pointerSize = 4;
            return;
        }

        const pointerSize = this.pointerSize;

        canv.ctx.fillStyle = "white";
        canv.ctx.strokeStyle = "#0B99FF";
        canv.ctx.lineWidth = (2 / canv.m[0]) * canv.dpr;

        const radius = 2.5;

        drawRoundedRect(
            canv.canvasController.startX - pointerSize / 2,
            canv.canvasController.startY - pointerSize / 2,
            pointerSize,
            pointerSize,
            (radius / canv.m[0]) * canv.dpr,
            true,
        );
        drawRoundedRect(
            canv.canvasController.startX - pointerSize / 2,
            canv.canvasController.startY - pointerSize / 2,
            pointerSize,
            pointerSize,
            (radius / canv.m[0]) * canv.dpr,
            false,
        );

        drawRoundedRect(
            canv.canvasController.startX + canv.canvasController.width - pointerSize / 2,
            canv.canvasController.startY - pointerSize / 2,
            pointerSize,
            pointerSize,
            (radius / canv.m[0]) * canv.dpr,
            true,
        );
        drawRoundedRect(
            canv.canvasController.startX + canv.canvasController.width - pointerSize / 2,
            canv.canvasController.startY - pointerSize / 2,
            pointerSize,
            pointerSize,
            (radius / canv.m[0]) * canv.dpr,
            false,
        );

        drawRoundedRect(
            canv.canvasController.startX + canv.canvasController.width - pointerSize / 2,
            canv.canvasController.startY + canv.canvasController.height - pointerSize / 2,
            pointerSize,
            pointerSize,
            (radius / canv.m[0]) * canv.dpr,
            true,
        );
        drawRoundedRect(
            canv.canvasController.startX + canv.canvasController.width - pointerSize / 2,
            canv.canvasController.startY + canv.canvasController.height - pointerSize / 2,
            pointerSize,
            pointerSize,
            (radius / canv.m[0]) * canv.dpr,
            false,
        );

        drawRoundedRect(
            canv.canvasController.startX - pointerSize / 2,
            canv.canvasController.startY + canv.canvasController.height - pointerSize / 2,
            pointerSize,
            pointerSize,
            (radius / canv.m[0]) * canv.dpr,
            true,
        );
        drawRoundedRect(
            canv.canvasController.startX - pointerSize / 2,
            canv.canvasController.startY + canv.canvasController.height - pointerSize / 2,
            pointerSize,
            pointerSize,
            (radius / canv.m[0]) * canv.dpr,
            false,
        );
    }

    drawOutline(isLow = false, width = 2.5) {
        if (canv.canvasController.selected.length === 0) return;

        if (
            canv.canvasController.selected.length === 1 &&
            canv.canvasController.selected[0].isItemWrapper() &&
            canv.canvasController.selected[0].onlyChild()?.disableOutline
        ) {
            return;
        }

        canv.ctx.strokeStyle = "#0B99FF";
        if (isLow) {
            canv.ctx.strokeStyle = "#BCE3FF";
        }
        canv.ctx.lineWidth = canv.actionManager.getZoomConstant(width);
        canv.ctx.strokeRect(
            canv.canvasController.startX,
            canv.canvasController.startY,
            canv.canvasController.width,
            canv.canvasController.height,
        );
    }

    drawSelecting(isLongSelecting = false) {
        canv.ctx.save();

        canv.ctx.strokeStyle = "#0B99FF";
        canv.ctx.lineWidth = 2 / canv.m[0];

        if (isLongSelecting) {
            canv.ctx.setLineDash([5 * canv.ctx.lineWidth, 5 * canv.ctx.lineWidth]);
        }

        canv.ctx.strokeRect(
            this.selectingStartX,
            this.selectingStartY,
            this.selectingWidth,
            this.selectingHeight,
        );

        canv.ctx.fillStyle = "#0B99FF";
        canv.ctx.globalAlpha = 0.2;
        canv.ctx.lineWidth = 2 / canv.m[0];
        canv.ctx.fillRect(
            this.selectingStartX,
            this.selectingStartY,
            this.selectingWidth,
            this.selectingHeight,
        );
        canv.ctx.globalAlpha = 1;

        canv.ctx.restore();
    }

    // Для выделения
    selectingStartX = 0;
    selectingStartY = 0;
    selectingWidth = 0;
    selectingHeight = 0;

    simulatedSelectingStartX = 0;
    simulatedSelectingStartY = 0;
    simulatedSelectingWidth = 0;
    simulatedSelectingHeight = 0;

    pointerSize = 4;
}

export const selectionManager = new SelectionManager();
